import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Callout } from '@node-space/storybook-components/dist/Callout'
import { ButtonProps } from '@node-space/storybook-components/dist/components/Button'
import { DoubleLabelSelect } from '@node-space/storybook-components/dist/DoubleLabelSelect'
import { Icon } from '@node-space/storybook-components/dist/Icon'
import { Input } from '@node-space/storybook-components/dist/Input'
import { ModalActions, ModalScrollable } from '@node-space/storybook-components/dist/Modal'
import { Radio, RadioProps } from '@node-space/storybook-components/dist/Radio'
import { SummaryDetail, SummaryRow } from '@node-space/storybook-components/dist/SummaryDetail'
import { logSentryError, subtractNumbers } from '@node-space/utils'
import LoadingAnimationDark from 'components/spinners/LoadingAnimationDark'
import { MERCHANT_PORTAL } from 'constants/General'
import { useAccountsQuery } from 'hooks/queries'
import { validateCryptoAddress } from 'services/WhitelistingService'
import { ErrorCodes } from 'types/errorCodes'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import { floorWithPrecision, formatString, isTestEnvironment, stringToDecimal } from 'utils/utils'
import { SendActionProps } from './Send'

const SendForm = ({
  dismissAction,
  setStep,
  wallets,
  form,
  loading,
  registerFormField,
  withdrawalLimits,
  addressValidated,
  setAddressValidated,
}: SendActionProps) => {
  const { t } = useTranslation()
  const [addressValidating, setAddressValidating] = useState(false)
  const formValues = form.watch()
  const { data: userAccount } = useAccountsQuery()

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.SEND_INITIATE, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
      cryptoWhitelisting: false,
    })
  }, [])

  const supportedWallets = wallets.filter(
    x => x.currency.code && x.currency.supportsWithdrawals && !x.currency.fiat
  )
  const selectedWallet =
    formValues.walletId && supportedWallets?.find(x => x.id.toString() === formValues.walletId)

  const insufficientBalance =
    selectedWallet?.balance < withdrawalLimits?.minimum ||
    withdrawalLimits?.maximum < withdrawalLimits?.minimum

  const actions: ButtonProps[] = [
    {
      children: t('cancel'),
      secondary: true,
      testid: 'cancel',
      onClick: () => dismissAction(),
    },
    {
      children: t('continue'),
      testid: t('continue'),
      disabled:
        !formValues.address ||
        !formValues.amount ||
        (selectedWallet?.lookup && !formValues.destinationTag) ||
        (selectedWallet?.alternatives?.length && !formValues.protocol) ||
        !!Object.keys(form.formState?.errors || {}).length,
      loading: addressValidating,
      onClick: () => setStep('confirm'),
    },
  ]

  // set radio options if more than 1 protocol detected for selected wallet e.g. TRC20 and ERC20 for USDT
  let radioProtocolProps: RadioProps
  if (selectedWallet?.currency?.protocols?.length) {
    const otherProtocols = selectedWallet.currency.protocols.filter(
      x => x.code !== selectedWallet.protocol
    )

    if (otherProtocols?.length) {
      radioProtocolProps = {
        label: t('selectNetwork'),
        name: 'protocol',
        options: [{ code: selectedWallet.protocol }, ...otherProtocols].map(x => ({
          label: x.code,
          value: x.code,
        })),
        borderless: false,
        value: formValues.protocol || selectedWallet.protocol,
        horizontal: true,
      }
    }
  }

  // calculate max withdraw amount from available balance minus withdrawal fee
  const maxAmount =
    selectedWallet && subtractNumbers(selectedWallet.available, selectedWallet.withdrawalFee)

  const validateAmount = (amount: number | string, onBlur = isTestEnvironment) => {
    let error = checkMinAmount(amount, onBlur)
    if (!error) {
      error = checkMaxAmount(amount)
    }
    if (error) {
      form.setError('amount', { message: error })
    } else {
      form.clearErrors('amount')
    }
  }

  const amountErrorMinimum =
    withdrawalLimits &&
    selectedWallet &&
    formatString(
      t('amountErrorMinimum'),
      floorWithPrecision(withdrawalLimits?.minimum, selectedWallet?.currency?.pricePrecision),
      selectedWallet?.currency?.code
    )

  const amountErrorMaximumFees =
    maxAmount &&
    selectedWallet &&
    formatString(
      t('amountErrorMaximumFees'),
      floorWithPrecision(maxAmount, selectedWallet?.currency?.pricePrecision),
      selectedWallet?.currency?.code
    )

  const amountErrorMaximum =
    withdrawalLimits &&
    selectedWallet &&
    formatString(
      t('amountErrorMaximum'),
      floorWithPrecision(withdrawalLimits?.maximum, selectedWallet?.currency?.pricePrecision),
      selectedWallet?.currency?.code
    )

  const checkMinAmount = (amount: number | string, onBlur = false) => {
    if (withdrawalLimits?.minimum && Number(amount) < withdrawalLimits?.minimum && onBlur) {
      return amountErrorMinimum
    }
  }

  const checkMaxAmount = (amount: number | string) => {
    // check amount against available balance minus withdrawal fee
    if (Number(amount) > maxAmount) {
      return amountErrorMaximumFees
    } else if (Number(amount) > withdrawalLimits?.maximum) {
      return amountErrorMaximum
    }
  }

  // action to set amount to a valid value based on validation errors
  const errorAction =
    !!form.formState?.errors?.amount?.message &&
    (form.formState.errors.amount.message === amountErrorMinimum
      ? () => setAmount(withdrawalLimits?.minimum)
      : form.formState.errors.amount.message === amountErrorMaximumFees
      ? () => setAmount(maxAmount)
      : form.formState.errors.amount.message === amountErrorMaximum &&
        (() => setAmount(withdrawalLimits?.maximum)))

  const setAmount = (amount: number | string) => {
    form.setValue('amount', amount)
    form.clearErrors('amount')
  }

  /**
   * Validate send address
   */
  const validateAddress = async (address: string) => {
    if (address && address !== addressValidated) {
      setAddressValidating(true)
      const addressArgs: { address: string; protocol: string; destinationTag?: string } = {
        address: formValues.address,
        protocol: formValues.protocol || selectedWallet?.protocol || selectedWallet?.currency?.name,
      }
      if (formValues.destinationTag) {
        addressArgs.destinationTag = formValues.destinationTag
      }
      const args = {
        currency: selectedWallet?.currency?.code,
        code: selectedWallet?.currency?.code,
        address: address,
        protocol: form.getValues('protocol'),
        accountReference: userAccount?.[0]?.reference,
        source: MERCHANT_PORTAL,
      }
      try {
        await validateCryptoAddress(args)
        setAddressValidated(address)
        setAddressValidating(false)
      } catch (error) {
        if (error?.data?.code === ErrorCodes.INVALID_ADDRESS) {
          form.setError('address', {
            message: `${t('addressValidationFailed')}`,
          })
        } else if (error?.data?.code === ErrorCodes.FAILED_FINCRIME_INVALID_ADDRESS) {
          form.setError('address', {
            message: `${t('cantProcessPayout')}`,
          })
        } else {
          form.setError('address', {
            message: `${t('oopsSomethingWentWrong')}`,
          })
        }
        logSentryError('Error from SendForm - validateAddress', error)
      }
    }
  }

  return (
    <div>
      <div className="pt-5 pb-7" data-testid="send-form">
        <ModalScrollable>
          <div className="px-6 space-y-6 my-px">
            {/* wallet selector */}
            <DoubleLabelSelect
              iconsUrl={process.env.ICONS_URL}
              name={t('selectWallet')}
              label={t('selectWallet')}
              menuMaxHeight={400}
              showOverflow
              placeholder={
                !wallets
                  ? t('loadingEllipses')
                  : wallets && wallets.length === 0
                  ? t('noWallets')
                  : t('searchOrSelectAWallet')
              }
              options={supportedWallets?.map(x => ({
                icon: x.currency?.code?.toLowerCase(),
                value: x.id?.toString(),
                label: x.description,
                secondLabel: formatString(
                  t('balanceWithCurrency'),
                  floorWithPrecision(x.balance, x.currency?.pricePrecision),
                  x.currency?.code
                ),
              }))}
              value={formValues.walletId}
              isSearchable={true}
              iconLeft={!formValues?.walletId && <Icon name="SearchIcon" />}
              onChange={(value: string) =>
                form.reset({ walletId: value }, { keepDefaultValues: true })
              }
            />
            {loading ? (
              <div className="flex items-center justify-center pt-10 pb-11">
                {LoadingAnimationDark}
              </div>
            ) : insufficientBalance ? (
              <Callout
                state="error"
                message={t('sendNotEnoughFunds', {
                  currencyCode: selectedWallet?.currency?.code,
                })}
              />
            ) : (
              selectedWallet && (
                <>
                  {/* protocol selector */}
                  {radioProtocolProps && (
                    <Radio
                      {...radioProtocolProps}
                      onChange={value => form.setValue('protocol', value)}
                    />
                  )}

                  {/* destination addresss */}
                  <div>
                    <Input
                      {...registerFormField('address')}
                      label={t('enterAddress')}
                      testid={t('enterAddress')}
                      placeholder={t('address')}
                      value={formValues.address || ''}
                      onBlur={e => validateAddress(e.target.value)}
                      onChange={e => {
                        form.clearErrors('address')
                        form.setValue('address', e.target.value)
                      }}
                    />
                    <div className="mt-2">{t('ensureSupportTransactions')}</div>
                  </div>

                  {/* destination tag */}
                  {selectedWallet.lookup && (
                    <Input
                      label={t('destinationTag')}
                      value={formValues.destinationTag}
                      onChange={e => form.setValue('destinationTag', e.target.value)}
                    />
                  )}

                  {/* amount */}
                  <Input
                    {...registerFormField('amount')}
                    label={t('amount')}
                    testid={t('amount')}
                    required
                    postLabel={selectedWallet && selectedWallet.currency.code}
                    bottomLeftLabel={
                      selectedWallet &&
                      withdrawalLimits?.minimum &&
                      formatString(
                        t('minimumAmount'),
                        floorWithPrecision(
                          withdrawalLimits?.minimum,
                          selectedWallet.currency.pricePrecision
                        ),
                        selectedWallet.currency.code
                      )
                    }
                    inputMode="decimal"
                    onBottomLeftLabelClick={() => {
                      validateAmount(withdrawalLimits?.minimum, true)
                      form.setValue('amount', withdrawalLimits?.minimum)
                    }}
                    onBlur={e => validateAmount(stringToDecimal(e.target.value), true)}
                    onChange={e => {
                      form.setValue('amount', stringToDecimal(e.target.value))
                      validateAmount(stringToDecimal(e.target.value))
                    }}
                    onErrorClick={errorAction ? () => errorAction() : null}
                  />

                  {/* details */}
                  <SummaryDetail>
                    <SummaryRow
                      label={t('sendAmount')}
                      testid="send-amount"
                      description={`${
                        formValues.amount
                          ? floorWithPrecision(
                              formValues?.amount,
                              selectedWallet?.currency?.pricePrecision
                            )
                          : ''
                      } ${selectedWallet?.currency?.code}`}
                    />
                    <SummaryRow
                      label={t('fee')}
                      testid="fee"
                      description={`${floorWithPrecision(
                        selectedWallet?.withdrawalFee,
                        selectedWallet?.currency?.pricePrecision
                      )} ${selectedWallet?.currency?.code}`}
                    />
                  </SummaryDetail>
                </>
              )
            )}
          </div>
        </ModalScrollable>
      </div>

      {/* actions */}
      <ModalActions actions={actions} currentStep={1} totalSteps={3} />
    </div>
  )
}

export default SendForm
